import {
  action,
  makeObservable,
  computed,
  observable,
  IObservableArray,
  toJS,
  runInAction,
} from "mobx";
import { IStoreElement } from "types/store";
import { ExtendedNextPageContext } from "types/app";
import { makeCancelable } from "helpers/makeCancelable";

export interface ISearch extends IStoreElement {
  value: string;
  help: IObservableArray<Entity.SearchAdvice>;
  list: {
    sts: IObservableArray;
    taps: IObservableArray;
    products: IObservableArray;
  };
  showSuggestions: boolean;
  totalProducts: number;
  fetch: () => Promise<void>;
  cancelLoad: () => void;
  toggleShowSuggestions: (value: boolean) => void;
  updateValue: (value: string) => void;
  clear: () => void;
  mounted: boolean;
  toggleMounted: () => void;
  fetchHelp: () => Promise<void>;
}

export default class Search implements ISearch {
  private ctx?: ExtendedNextPageContext;
  @observable value = "";
  @observable mounted = false;
  @observable help = [] as IObservableArray<Entity.SearchAdvice>;
  @observable list = {
    sts: [] as IObservableArray,
    taps: [] as IObservableArray,
    products: [] as IObservableArray,
  };

  @observable showSuggestions = false;
  @observable preventOpen = false;

  _promise = null;

  constructor() {
    makeObservable(this);
  }

  @action
  async fetch(): Promise<void> {
    try {
      const promise = this.ctx.api.methods.search.searchAutocomplete({
        regionID: this.ctx.store.location.regionID,
        query: this.value,
      });

      this._promise = makeCancelable(promise);

      const { data, errors } = await this._promise;

      if (data) {
        this.setSuggestions(data);
      }

      if (errors) {
        this.ctx.store.notifications.show({
          type: "toast",
          props: {
            title: "Ошибка запроса",
            children: "Попробуйте позже",
          },
        });
      }
    } catch (err) {
      // catch error of the makeCancelable function
    } finally {
      this.preventOpen = false;
    }
  }

  cancelLoad(): void {
    if (this._promise) this._promise.cancel();
  }

  @action
  updateValue(value: string): void {
    this.value = value;
  }

  @action
  setSuggestions(data): void {
    this.list = {
      sts: data.autocomplete?.sts || [],
      taps: data.autocomplete?.taps || [],
      products: data.autocomplete?.products || [],
    };
  }

  @action
  async clear(): Promise<void> {
    runInAction(() => {
      this.value = "";
      this.showSuggestions = false;
      this.list = {
        sts: [] as IObservableArray,
        taps: [] as IObservableArray,
        products: [] as IObservableArray,
      };
    });

    this.fetch();
  }

  @action
  toggleShowSuggestions(value: boolean): void {
    this.showSuggestions = value;
  }

  @action
  toggleMounted(): void {
    this.mounted = true;
  }

  @computed
  get suggestions(): any {
    return toJS(this.list);
  }

  @computed
  get totalProducts(): number {
    return this.list.products.length;
  }

  @action
  async fetchHelp(): Promise<void> {
    const { data } = await this.ctx.api.methods.search.searchHelp();

    if (data && data.searchHelp) {
      runInAction(() => {
        this.help = data.searchHelp as IObservableArray<Entity.SearchAdvice>;
      });
    }
  }
}
